import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, Sparkles, Loader2, Crown, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const Matches = () => {
  const [matches, setMatches] = useState([]);
  const [likesReceived, setLikesReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLikes, setLoadingLikes] = useState(false);

  const { user, token } = useAuth();

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatches(res.data);
    } catch (err) {
      toast.error('Kunne ikke indlæse matches');
    } finally {
      setLoading(false);
    }
  };

  const fetchLikesReceived = async () => {
    if (!user?.is_premium) return;
    setLoadingLikes(true);
    try {
      const res = await axios.get(`${API}/likes-received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikesReceived(res.data);
    } catch (err) {
      // Premium feature error handled
    } finally {
      setLoadingLikes(false);
    }
  };

  useEffect(() => {
    fetchMatches();
    if (user?.is_premium) {
      fetchLikesReceived();
    }
  }, [user?.is_premium]);

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-primary fill-primary" />
            <span className="text-lg font-display font-semibold text-foreground">Matches</span>
          </div>
        </div>
      </div>

      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          <Tabs defaultValue="matches" className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-full p-1 bg-muted">
              <TabsTrigger value="matches" className="rounded-full" data-testid="matches-tab">
                Matches ({matches.length})
              </TabsTrigger>
              <TabsTrigger value="likes" className="rounded-full" data-testid="likes-tab">
                <Crown className="w-4 h-4 mr-1" />
                Likes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="matches" className="mt-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : matches.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-display font-semibold text-foreground">Ingen matches endnu</h3>
                  <p className="text-muted-foreground mt-2 max-w-xs mx-auto">
                    Fortsæt med at swipe for at finde dit perfekte match!
                  </p>
                  <Link to="/discover">
                    <Button className="mt-6 rounded-full bg-primary hover:bg-primary/90" data-testid="start-swiping-btn">
                      Start med at swipe
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {matches.map((match, index) => (
                    <motion.div
                      key={match.match_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link to={`/chat/${match.user.id}`}>
                        <div className="flex items-center gap-4 p-4 bg-card rounded-2xl border border-border hover:border-primary/50 transition-colors" data-testid={`match-${match.user.id}`}>
                          <div className="relative">
                            <img
                              src={match.user.photos?.[0] || 'https://via.placeholder.com/80'}
                              alt={match.user.name}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                            {match.user.is_premium && (
                              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                <Sparkles className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-foreground truncate">
                                {match.user.name}, {match.user.age || '??'}
                              </h4>
                              {match.is_boost_match && (
                                <span className="inline-flex items-center gap-1 bg-gradient-to-r from-accent to-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  <Zap className="w-3 h-3" />
                                  Boost
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {match.user.bio || 'Ingen bio endnu'}
                            </p>
                            <p className="text-xs text-primary mt-1">
                              Matchede {new Date(match.matched_at).toLocaleDateString('da-DK')}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon" className="rounded-full">
                            <MessageCircle className="w-5 h-5 text-primary" />
                          </Button>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="likes" className="mt-6">
              {!user?.is_premium ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="text-xl font-display font-semibold text-foreground">Premium Funktion</h3>
                  <p className="text-muted-foreground mt-2 max-w-xs mx-auto">
                    Opgrader for at se hvem der har liket din profil før du swiper!
                  </p>
                  <Link to="/premium">
                    <Button className="mt-6 rounded-full bg-primary hover:bg-primary/90" data-testid="upgrade-btn">
                      <Crown className="w-4 h-4 mr-2" />
                      Opgrader til Premium
                    </Button>
                  </Link>
                </div>
              ) : loadingLikes ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : likesReceived.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-display font-semibold text-foreground">Ingen likes endnu</h3>
                  <p className="text-muted-foreground mt-2">
                    Fuldfør din profil for at tiltrække flere likes!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {likesReceived.map((user, index) => (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link to={`/profile/${user.id}`}>
                        <div className="relative aspect-[3/4] rounded-2xl overflow-hidden group" data-testid={`like-${user.id}`}>
                          <img
                            src={user.photos?.[0] || 'https://via.placeholder.com/200'}
                            alt={user.name}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <h4 className="font-semibold text-white">
                              {user.name}, {user.age || '??'}
                            </h4>
                          </div>
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-primary text-white">
                              <Heart className="w-3 h-3 mr-1 fill-white" />
                              Likede dig
                            </Badge>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Matches;
